import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { AlertTriangle, X } from "lucide-react";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : "—";

export default function TakeoverNoticeBanner() {
  const [notices, setNotices] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/cases/takeover-notices")
      .then(({ data }) => { if (alive) setNotices(data.notices || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!notices.length) return null;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/cases/takeover-notices/ack", { case_ids: notices.map((n) => n.id) });
      setNotices([]);
    } catch {
      setDismissing(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50/80 p-5"
      data-testid="takeover-notice-banner"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-lg bg-amber-500 flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-white" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-zinc-900">
            {notices.length === 1 ? "Eine Akte wurde übernommen" : `${notices.length} Akten wurden übernommen`}
          </div>
          <p className="text-sm text-zinc-700 mt-0.5">
            Die folgenden Akten wurden von EasyGutachter übernommen und aus deiner Aktenliste entfernt.
            Das Honorar dieser Akten wird mit offenen Abo-Rechnungen verrechnet.
          </p>
          <ul className="mt-3 space-y-2">
            {notices.map((n) => (
              <li
                key={n.id}
                className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                data-testid={`takeover-notice-${n.id}`}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-semibold text-zinc-900">
                    {n.case_reference || n.case_number || n.title || "Akte"}
                  </span>
                  <span className="text-zinc-500">· übernommen am {fmtDate(n.taken_over_at)}</span>
                  {n.taken_over_by_admin_name && (
                    <span className="text-zinc-500">von {n.taken_over_by_admin_name}</span>
                  )}
                </div>
                {n.taken_over_reason && (
                  <div className="text-xs text-zinc-600 mt-0.5">Grund: {n.taken_over_reason}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={dismiss}
          disabled={dismissing}
          className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
          data-testid="takeover-notice-dismiss-btn"
        >
          <X className="h-4 w-4 mr-1" /> Verstanden
        </Button>
      </div>
    </div>
  );
}
